import type { Ref } from 'vue';

import type { Country, Selection } from '@/features/destination';
import type { DepartureCity, DepartureCityType } from '@/features/departure-city';
import { normalizeDateFrom } from '@/features/date-from';
import type { DateFrom } from '@/features/date-from';
import { normalizeRange } from '@/features/nights-range';
import type { NightsRange } from '@/features/nights-range';
import {
  dateFromEqual,
  nightsEqual,
  resolveDefaultCriteria,
  resolveInitialDateFrom,
  resolveSourceCountryId,
} from '../lib/defaultCriteria';
import type { DefaultCriteriaIndex } from '../lib/defaultCriteria';
import {
  CHANGED_FIELD,
  addChangedField,
  clearChangedFields,
  isFieldChanged,
} from '../lib/tourCriteriaCookie';

export interface UseCrossFieldCriteriaOptions {
  countries: Ref<Country[]>;
  defaultCriteria: Ref<DefaultCriteriaIndex>;
  destination: Ref<Selection | null>;
  nights: Ref<NightsRange>;
  dateFrom: Ref<DateFrom | null>;
}

function cityHasType(type: DepartureCityType | undefined): boolean {
  return type === 'capital' || type === 'region';
}

export function useCrossFieldCriteria(
  options: UseCrossFieldCriteriaOptions,
) {
  const { countries, defaultCriteria, destination, nights, dateFrom } = options;

  const sourceCountryId = (): number | undefined =>
    resolveSourceCountryId(destination.value, countries.value);

  const applyNightsDefault = (defaultNights: NightsRange | undefined): void => {
    if (!defaultNights || isFieldChanged(CHANGED_FIELD.nights)) {
      return;
    }
    const next = normalizeRange(defaultNights);
    if (!nightsEqual(next, nights.value)) {
      nights.value = next;
    }
  };

  const applyDateDefault = (
    type: DepartureCityType | undefined,
    defaultDate: DateFrom | undefined,
  ): void => {
    if (!cityHasType(type) || !defaultDate || isFieldChanged(CHANGED_FIELD.date)) {
      return;
    }
    const next = normalizeDateFrom(defaultDate);
    if (!dateFromEqual(next, dateFrom.value)) {
      dateFrom.value = next;
    }
  };

  const applyDefaults = (city: DepartureCity | null): void => {
    const def = resolveDefaultCriteria(defaultCriteria.value, sourceCountryId(), city?.type);
    applyNightsDefault(def.nights);
    applyDateDefault(city?.type, def.dateFrom);
  };

  const applyInitialDate = (city: DepartureCity | null): void => {
    if (isFieldChanged(CHANGED_FIELD.date)) {
      return;
    }
    const initial = resolveInitialDateFrom(defaultCriteria.value, sourceCountryId(), city?.type);
    if (!initial) {
      return;
    }
    const next = normalizeDateFrom(initial);
    if (!dateFromEqual(next, dateFrom.value)) {
      dateFrom.value = next;
    }
  };

  const markTouchedNights = () => {
    addChangedField(CHANGED_FIELD.nights);
  };

  const markTouchedDate = () => {
    addChangedField(CHANGED_FIELD.date);
  };

  const reset = (): void => {
    clearChangedFields();
  };

  return {
    applyDefaults,
    markTouchedNights,
    markTouchedDate,
    addChangedField,
    applyInitialDate,
    reset,
  };
}
